import moment from "moment-timezone";
import { agencySaleDetail } from "../services/agencies-sales-details";
import {
  HistoryFilterInput,
  SalesHistoryPricing,
  StatementRow,
  StatementSummary,
} from "../services/agencies-sales-details/types";
import { t } from "../i18n";

// Download of the filtered seller statement (sales + conversions + every other
// movement of the coin stock). Rows, totals, opening/closing and the BRL price
// come from the server for the WHOLE filter (the screen list is paginated).
// Libraries and fonts load only when the seller clicks.

export interface ExportContext {
  filters: HistoryFilterInput;
  filterLabel: string;
  agencyName: string;
  zone: string;
  // the seller's own days picked in the filter ("YYYY-MM-DD"), for the file name
  dayFrom?: string;
  dayTo?: string;
}

const fmtInt = (value: number) => Math.round(value).toLocaleString("pt-BR");
const fmtSigned = (value: number) => `${value > 0 ? "+" : value < 0 ? "-" : ""}${fmtInt(Math.abs(value))}`;
const fmtBrl = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Sale value in BRL from the price the server returns (coins per R$ 1).
export const saleValueBrl = (coins: number, pricing: SalesHistoryPricing | null) =>
  pricing && pricing.coinsPerBrl > 0 ? Math.round((coins / pricing.coinsPerBrl) * 100) / 100 : null;

const fetchExport = async (filters: HistoryFilterInput) => {
  const response = await agencySaleDetail.getStatementExport(filters);
  if (response?.status !== "success" || !response.data) {
    throw new Error(response?.error ?? "export_failed");
  }
  return response.data;
};

export const kindLabel = (row: StatementRow) =>
  t(`history.kind.${row.kind}` as any);

interface Line {
  when: Date | null; // seller's wall-clock, stored as UTC fields (Excel has no zones)
  whenText: string;
  type: string;
  kind: StatementRow["kind"];
  saleId: number | null;
  buyerId: number | null;
  buyerName: string;
  coinsSold: number | null;
  valueBrl: number | null;
  movement: number;
  pointsSpent: number | null;
  balanceAfter: number | null;
  sold: boolean;
  status: string;
}

const toLines = (rows: StatementRow[], pricing: SalesHistoryPricing | null, zone: string): Line[] =>
  rows.map((row) => {
    const local = row.at ? moment(String(row.at).replace(" ", "T")).tz(zone) : null;
    const reversed = row.reversedAt
      ? moment(String(row.reversedAt).replace(" ", "T")).tz(zone).format("DD/MM/YYYY HH:mm")
      : null;
    const isSale = row.kind === "sale";
    const coinsSold = isSale ? Math.abs(row.coinsDelta) : null;
    let status = "";
    if (isSale) {
      status = reversed
        ? t("history.export.statusReversed", { date: reversed })
        : t("history.export.statusSold");
    } else if (row.kind === "reversal") {
      status = t("history.export.statusReversalOf", { id: row.saleId ?? "?" });
    } else if (row.kind === "conversion") {
      status = row.pointsSpent != null ? t("history.export.statusConversion", { points: fmtInt(row.pointsSpent) }) : "";
    } else if (row.kind === "unrecorded") {
      status = t("history.export.statusUnrecorded");
    } else {
      status = row.description ?? "";
    }
    return {
      when: local?.isValid() ? new Date(`${local.format("YYYY-MM-DDTHH:mm:ss")}Z`) : null,
      whenText: local?.isValid() ? local.format("DD/MM/YYYY HH:mm") : "",
      type: kindLabel(row),
      kind: row.kind,
      saleId: row.saleId ?? null,
      buyerId: row.buyerId ?? null,
      buyerName: row.buyerUsername ?? "",
      coinsSold,
      valueBrl: isSale && coinsSold != null ? saleValueBrl(coinsSold, pricing) : null,
      movement: row.coinsDelta,
      pointsSpent: row.kind === "conversion" ? row.pointsSpent ?? null : null,
      balanceAfter: row.balanceAfter ?? null,
      sold: isSale && !reversed,
      status,
    };
  });

const titleLines = (
  ctx: ExportContext,
  pricing: SalesHistoryPricing | null,
  truncated: boolean,
  limit: number
) => {
  const lines = [
    `${t("history.export.title")} — ${ctx.agencyName}`,
    `${t("history.export.period")}: ${ctx.filterLabel}`,
  ];
  if (pricing) lines.push(t("history.export.priceLine", { coins: fmtInt(pricing.coinsPerBrl) }));
  lines.push(t("history.export.generatedAt", { date: moment.tz(ctx.zone).format("DD/MM/YYYY HH:mm") }));
  if (truncated) lines.push(t("history.export.truncated", { limit: fmtInt(limit) }));
  return lines;
};

const fileBase = (ctx: ExportContext) => {
  const parts = ["sales-history"];
  if (ctx.filters.userId) parts.push(`user-${ctx.filters.userId}`);
  parts.push(ctx.dayFrom || "start");
  parts.push(ctx.dayTo || moment.tz(ctx.zone).format("YYYY-MM-DD"));
  return parts.join("_");
};

const download = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

// Totals computed here too (Excel recalculates its formulas; viewers that do not
// recalculate show these cached values).
const totalsOf = (lines: Line[]) => {
  const sold = lines.filter((l) => l.sold);
  const conv = lines.filter((l) => l.kind === "conversion");
  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);
  return {
    soldCount: sold.length,
    soldCoins: sum(sold.map((l) => l.coinsSold ?? 0)),
    soldValue: Math.round(sum(sold.map((l) => l.valueBrl ?? 0)) * 100) / 100,
    convCount: conv.length,
    convCoins: sum(conv.map((l) => l.movement)),
    convPoints: sum(conv.map((l) => l.pointsSpent ?? 0)),
    movement: sum(lines.map((l) => l.movement)),
  };
};

// Real .xlsx: title block, header frozen, AutoFilter on every column (Type is
// filterable), date / integer / BRL formats, widths, and subtotal rows with
// formulas: sales (sold only), conversions (own subtotal), opening/closing check.
export const exportSalesHistoryXlsx = async (ctx: ExportContext) => {
  const [{ rows, truncated, limit, pricing, summary, balanceMode }, ExcelJSModule] = await Promise.all([
    fetchExport(ctx.filters),
    import("exceljs"),
  ]);
  const ExcelJS = (ExcelJSModule as any).default ?? ExcelJSModule;
  const lines = toLines(rows, pricing, ctx.zone);
  const statement = summary?.statement as StatementSummary | undefined;
  const totals = totalsOf(lines);

  const book = new ExcelJS.Workbook();
  book.creator = "Didder";
  const sheet = book.addWorksheet(t("history.export.sheetName"));

  titleLines(ctx, pricing, truncated, limit).forEach((text, index) => {
    const row = sheet.addRow([text]);
    row.font = index === 0 ? { bold: true, size: 14 } : { size: 10, color: { argb: "FF555555" } };
  });
  let openingRowNumber = 0;
  if (balanceMode && statement?.opening != null) {
    const row = sheet.addRow([t("history.export.opening"), null, null, null, null, null, null, null, null, statement.opening]);
    row.font = { bold: true };
    row.getCell(10).numFmt = "#,##0";
    openingRowNumber = row.number;
  }
  sheet.addRow([]);

  // columns: A date, B type, C sale, D buyer id, E buyer, F coins sold, G value,
  // H stock movement, I points, J balance after, K status
  const head = [
    t("history.export.colDate"),
    t("history.export.colType"),
    t("history.export.colSaleId"),
    t("history.export.colBuyerId"),
    t("history.export.colBuyerName"),
    t("history.export.colCoinsSold"),
    t("history.export.colValue"),
    t("history.export.colMovement"),
    t("history.export.colPoints"),
    t("history.export.colBalance"),
    t("history.export.colStatus"),
  ];
  const headerRow = sheet.addRow(head);
  const headerIndex = headerRow.number;
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.alignment = { vertical: "middle", wrapText: true };
  headerRow.height = 30;
  headerRow.eachCell((cell: any) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF3F51B5" } };
  });

  const KIND_COLOR: Record<string, string> = {
    conversion: "FFE8F5E9",
    reversal: "FFFFF3E0",
    other: "FFF3E5F5",
    unrecorded: "FFFFEBEE",
  };
  lines.forEach((l) => {
    const row = sheet.addRow([
      l.when ?? l.whenText,
      l.type,
      l.saleId,
      l.buyerId,
      l.buyerName,
      l.coinsSold,
      l.valueBrl,
      l.movement,
      l.pointsSpent,
      l.balanceAfter,
      l.status,
    ]);
    if (l.kind === "sale" && !l.sold) row.font = { color: { argb: "FF9E9E9E" }, strike: true };
    const fill = KIND_COLOR[l.kind];
    if (fill) row.eachCell({ includeEmpty: true }, (cell: any) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
    });
  });

  const first = headerIndex + 1;
  const last = Math.max(headerIndex + lines.length, first);
  const has = lines.length > 0;
  const q = (s: string) => s.replace(/"/g, '""');
  const saleType = q(t("history.kind.sale"));
  const convType = q(t("history.kind.conversion"));
  const sold = q(t("history.export.statusSold"));
  const rng = (col: string) => `${col}${first}:${col}${last}`;
  const f = (formula: string, result: number) => (has ? { formula, result } : result);

  const totalRow = (label: string, cells: Record<number, any>, hint?: string) => {
    const row = sheet.addRow([]);
    row.getCell(1).value = label;
    Object.entries(cells).forEach(([col, v]) => (row.getCell(Number(col)).value = v));
    if (hint) row.getCell(11).value = hint;
    row.font = { bold: true };
    row.eachCell({ includeEmpty: true }, (cell: any) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAF6" } };
    });
    return row;
  };

  const salesRow = totalRow(t("history.export.totalsSales"), {
    2: f(`COUNTIFS(${rng("B")},"${saleType}",${rng("K")},"${sold}")`, totals.soldCount),
    6: f(`SUMIFS(${rng("F")},${rng("B")},"${saleType}",${rng("K")},"${sold}")`, totals.soldCoins),
    7: f(`SUMIFS(${rng("G")},${rng("B")},"${saleType}",${rng("K")},"${sold}")`, totals.soldValue),
  }, t("history.export.totalsHint"));
  salesRow.getCell(1).border = { top: { style: "medium", color: { argb: "FF3F51B5" } } };
  totalRow(t("history.export.totalsConversions"), {
    2: f(`COUNTIF(${rng("B")},"${convType}")`, totals.convCount),
    8: f(`SUMIFS(${rng("H")},${rng("B")},"${convType}")`, totals.convCoins),
    9: f(`SUMIFS(${rng("I")},${rng("B")},"${convType}")`, totals.convPoints),
  });
  if (balanceMode && statement?.opening != null && statement.closing != null) {
    const movementRow = totalRow(t("history.export.totalsMovement"), { 8: f(`SUM(${rng("H")})`, totals.movement) }).number;
    const closingRow = totalRow(t("history.export.closing"), { 10: statement.closing }).number;
    // opening + every movement - closing: must be 0
    totalRow(t("history.export.check"), {
      10: { formula: `J${openingRowNumber}+H${movementRow}-J${closingRow}`, result: statement.difference ?? 0 },
    });
  }

  const widths = [17, 13, 9, 11, 26, 13, 13, 14, 13, 14, 30];
  widths.forEach((w, i) => (sheet.getColumn(i + 1).width = w));
  sheet.getColumn(1).numFmt = "dd/mm/yyyy hh:mm";
  [3, 4].forEach((c) => (sheet.getColumn(c).numFmt = "0"));
  [6, 9, 10].forEach((c) => (sheet.getColumn(c).numFmt = "#,##0"));
  sheet.getColumn(8).numFmt = "+#,##0;-#,##0;0";
  sheet.getColumn(7).numFmt = '"R$" #,##0.00';

  sheet.views = [{ state: "frozen", ySplit: headerIndex }];
  sheet.autoFilter = { from: { row: headerIndex, column: 1 }, to: { row: last, column: head.length } };

  const buffer = await book.xlsx.writeBuffer();
  download(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `${fileBase(ctx)}.xlsx`
  );
  return lines.length;
};

// A4 statement: title with seller + period, opening balance, one row per movement
// with the balance after it, subtotals and closing balance at the end. Names keep
// accents/symbols (embedded Noto Sans), styled letters are folded (NFKC) and emoji
// are drawn as images (see pdf-unicode.ts).
export const exportSalesHistoryPdf = async (ctx: ExportContext) => {
  const [{ rows, truncated, limit, pricing, summary, balanceMode }, { jsPDF }, autoTableModule, unicode] =
    await Promise.all([
      fetchExport(ctx.filters),
      import("jspdf"),
      import("jspdf-autotable"),
      import("./pdf-unicode"),
    ]);
  const autoTable = autoTableModule.default;
  const lines = toLines(rows, pricing, ctx.zone);
  const statement = summary?.statement as StatementSummary | undefined;
  const totals = totalsOf(lines);

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const u = await unicode.setupUnicodePdf(doc);
  const font = unicode.PDF_FONT;

  let y = 40;
  titleLines(ctx, pricing, truncated, limit).forEach((text, index) => {
    doc.setFont(font, index === 0 ? "bold" : "normal");
    doc.setFontSize(index === 0 ? 14 : 9);
    doc.setTextColor(index === 0 ? 20 : 90);
    if (index === 0) u.drawLine(doc, text, 40, y, 14, 700);
    else doc.text(u.plain(text), 40, y);
    y += index === 0 ? 18 : 13;
  });
  if (balanceMode && statement?.opening != null) {
    doc.setFont(font, "bold");
    doc.setFontSize(10);
    doc.setTextColor(20);
    doc.text(`${t("history.export.opening")}: ${fmtInt(statement.opening)}`, 40, y + 4);
    y += 14;
  }

  const head = [
    t("history.export.colDate"),
    t("history.export.colType"),
    t("history.export.colSaleId"),
    t("history.export.colBuyerId"),
    t("history.export.colBuyerName"),
    t("history.export.colCoinsSold"),
    t("history.export.colValue"),
    t("history.export.colMovement"),
    t("history.export.colPoints"),
    ...(balanceMode ? [t("history.export.colBalance")] : []),
    t("history.export.colStatus"),
  ];
  const NAME = 4;
  const body = lines.map((l) => [
    l.whenText,
    l.type,
    l.saleId != null ? String(l.saleId) : "",
    l.buyerId != null ? String(l.buyerId) : "",
    u.plain(l.buyerName), // placeholder for layout; the real name is drawn in didDrawCell
    l.coinsSold != null ? fmtInt(l.coinsSold) : "",
    l.valueBrl != null ? fmtBrl(l.valueBrl) : "",
    fmtSigned(l.movement),
    l.pointsSpent != null ? fmtInt(l.pointsSpent) : "",
    ...(balanceMode ? [l.balanceAfter != null ? fmtInt(l.balanceAfter) : ""] : []),
    u.plain(l.status),
  ]);
  // Foot rows: the label spans date..buyer name (5 columns), then the same
  // columns as the body.
  const LABEL_SPAN = 5;
  const footRow = (label: string, values: Record<number, string | undefined>) => {
    const r: any[] = [{ content: label, colSpan: LABEL_SPAN }];
    for (let i = LABEL_SPAN; i < head.length; i++) r.push(values[i] ?? "");
    return r;
  };
  const balanceCol = 9;
  const statusCol = head.length - 1;
  const foot = [
    footRow(`${t("history.export.totalsSales")} — ${t("history.summary.salesCount", { count: totals.soldCount })}`, {
      5: fmtInt(totals.soldCoins),
      ...(pricing ? { 6: fmtBrl(totals.soldValue) } : {}),
      [statusCol]: t("history.export.totalsHint"),
    }),
    footRow(`${t("history.export.totalsConversions")} — ${t("history.summary.conversionsCount", { count: totals.convCount })}`, {
      7: fmtSigned(totals.convCoins),
      8: fmtInt(totals.convPoints),
    }),
    ...(balanceMode && statement?.opening != null && statement.closing != null
      ? [
          footRow(t("history.export.totalsMovement"), { 7: fmtSigned(totals.movement) }),
          footRow(t("history.export.closing"), { [balanceCol]: fmtInt(statement.closing) }),
          footRow(t("history.export.check"), {
            [statusCol]: t("history.export.checkLine", {
              opening: fmtInt(statement.opening),
              movement: fmtSigned(totals.movement),
              closing: fmtInt(statement.closing),
            }),
          }),
        ]
      : []),
  ];

  const ROW_FILL: Record<string, [number, number, number]> = {
    conversion: [232, 245, 233],
    reversal: [255, 243, 224],
    other: [243, 229, 245],
    unrecorded: [255, 235, 238],
  };

  autoTable(doc, {
    startY: y + 4,
    head: [head],
    body,
    foot,
    showFoot: "lastPage",
    theme: "grid",
    styles: { font, fontSize: 7, cellPadding: 2.5, overflow: "ellipsize", lineColor: [225, 225, 235], lineWidth: 0.4 },
    headStyles: { font, fontStyle: "bold", fillColor: [63, 81, 181], textColor: 255, overflow: "linebreak", valign: "middle" },
    footStyles: { font, fontStyle: "bold", fillColor: [232, 234, 246], textColor: 20 },
    columnStyles: {
      0: { cellWidth: 62 },
      1: { cellWidth: 74, overflow: "linebreak" },
      2: { cellWidth: 34 },
      3: { cellWidth: 46 },
      [NAME]: { cellWidth: 112 },
      5: { halign: "right", cellWidth: 52 },
      6: { halign: "right", cellWidth: 50 },
      7: { halign: "right", cellWidth: 56 },
      8: { halign: "right", cellWidth: 52 },
      ...(balanceMode ? { 9: { halign: "right", cellWidth: 58 } } : {}),
      [head.length - 1]: { overflow: "linebreak" },
    },
    margin: { left: 30, right: 30 },
    didParseCell: (data: any) => {
      if (data.section !== "body") return;
      const line = lines[data.row.index];
      const fill = line && ROW_FILL[line.kind];
      if (fill) data.cell.styles.fillColor = fill;
      if (line?.kind === "sale" && !line.sold) data.cell.styles.textColor = [158, 158, 158];
    },
    willDrawCell: (data: any) => {
      // the buyer name is drawn by didDrawCell (emoji/other scripts as images)
      if (data.section === "body" && data.column.index === NAME) data.cell.text = [];
    },
    didDrawCell: (data: any) => {
      if (data.section !== "body" || data.column.index !== NAME) return;
      const line = lines[data.row.index];
      if (!line?.buyerName) return;
      const cell = data.cell;
      const pad = 2.5;
      const size = 7;
      const muted = line.kind === "sale" && !line.sold;
      doc.setTextColor(muted ? 158 : 40);
      u.drawLine(doc, line.buyerName, cell.x + pad, cell.y + cell.height / 2 + size * 0.35, size, cell.width - pad * 2);
      doc.setTextColor(40);
    },
    didDrawPage: () => {
      const page = doc.getNumberOfPages();
      doc.setFont(font, "normal");
      doc.setFontSize(8);
      doc.setTextColor(140);
      doc.text(`${page}`, doc.internal.pageSize.getWidth() - 40, doc.internal.pageSize.getHeight() - 18);
    },
  });

  download(doc.output("blob"), `${fileBase(ctx)}.pdf`);
  return lines.length;
};
